"use client";

import { useMemo } from "react";

import { MAP_LEVEL_DRAFT_NEW_ID_PREFIX } from "@/app/(internal)/panel/(maps)/map/_hooks/use-map-level-boundaries-draft";
import { geometryGeoJsonToPolygon } from "@/app/(internal)/panel/(maps)/map/_utils/geometry-geojson-to-polygon";
import type { CustomLayersPreviewBoundaryPanelPayload } from "@/app/(internal)/panel/data/custom-layers/preview/_hooks/use-custom-layers-preview-boundary-panel-payload";
import { SelectedDataType } from "@/components/maps/map-panel/map-panel-context";
import { getFirstRingFromGeometry } from "@/providers/maps/utils/get-first-ring-from-geometry";
import { useGetMapLevelQuery } from "@/providers/store/api/map-levels/map-levels";

import { useBoundariesEditMode } from "../../_contexts/boundaries-edit-mode-context";
import { useSelectedDataType } from "../../_contexts/selected-data-type-context";
import { useMapLevelBoundaryPanel } from "../_contexts/map-level-boundary-panel-context";

interface UseWorkMapMapLevelBoundaryPanelPayloadParams {
  districtId: string | null;
}

function readDraftStringProp(
  draftProps: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = draftProps[key];
  return typeof value === "string" ? value : undefined;
}

/** Name / note / preview for the working-map map-level boundary edit panel. */
export function useWorkMapMapLevelBoundaryPanelPayload({
  districtId,
}: UseWorkMapMapLevelBoundaryPanelPayloadParams) {
  const { isBoundariesEditMode, editingKind, editingLayerId } = useBoundariesEditMode();
  const { selectedDataType } = useSelectedDataType();
  const { selectedBoundaryId, getDraftFeatureByBoundaryId } = useMapLevelBoundaryPanel();

  const isMapLevelBoundaryPanel =
    isBoundariesEditMode &&
    editingKind === "mapLevel" &&
    selectedDataType === SelectedDataType.BOUNDARY &&
    selectedBoundaryId != null &&
    editingLayerId != null;

  const { data: mapLevel, isLoading, isFetching } = useGetMapLevelQuery(
    {
      districtId: districtId ?? "",
      levelId: editingLayerId ?? "",
    },
    {
      skip: !isMapLevelBoundaryPanel || !districtId || !editingLayerId,
    },
  );

  const boundaryPayload = useMemo((): CustomLayersPreviewBoundaryPanelPayload | null => {
    if (!isMapLevelBoundaryPanel || selectedBoundaryId == null || editingLayerId == null) {
      return null;
    }

    const isNewDraftBoundary = selectedBoundaryId.startsWith(MAP_LEVEL_DRAFT_NEW_ID_PREFIX);
    const draftFeature = getDraftFeatureByBoundaryId(selectedBoundaryId);
    const draftProps = (draftFeature?.properties ?? {}) as Record<string, unknown>;
    const draftName = readDraftStringProp(draftProps, "name");
    const draftDescription = readDraftStringProp(draftProps, "description");
    const draftColor = readDraftStringProp(draftProps, "color");

    const boundary = mapLevel?.boundaries?.find((item) => item.id === selectedBoundaryId);

    const previewCoordinates =
      (draftFeature != null
        ? getFirstRingFromGeometry(draftFeature.geometry)
        : null) ??
      (boundary?.geometryGeoJson
        ? getFirstRingFromGeometry(geometryGeoJsonToPolygon(boundary.geometryGeoJson))
        : undefined) ??
      undefined;

    if (boundary != null) {
      return {
        boundaryId: selectedBoundaryId,
        mapLevelId: editingLayerId,
        name: draftName && draftName !== "" ? draftName : boundary.name,
        note: draftDescription ?? boundary.description ?? "",
        color: draftColor ?? boundary.color ?? null,
        previewCoordinates,
        isNewDraftBoundary: false,
      };
    }

    return {
      boundaryId: selectedBoundaryId,
      mapLevelId: editingLayerId,
      name: draftName ?? "",
      note: draftDescription ?? "",
      color: draftColor && draftColor !== "" ? draftColor : null,
      previewCoordinates,
      isNewDraftBoundary,
    };
  }, [
    editingLayerId,
    getDraftFeatureByBoundaryId,
    isMapLevelBoundaryPanel,
    mapLevel?.boundaries,
    selectedBoundaryId,
  ]);

  return {
    boundaryPayload,
    isLoadingBoundaryDetails:
      isMapLevelBoundaryPanel && boundaryPayload == null && (isLoading || isFetching),
    isMapLevelBoundaryPanel,
  };
}
